
/* ==========================================================================
   Italian Bear Chocolate — guided enquiry flow controller
   assets/enquiry-flow.js
   --------------------------------------------------------------------------
   Loaded once per page by sections/enquiry-flow.liquid. Knows nothing about
   chocolate: every customer-facing string comes from the section's JSON
   content block (business copy in "nodes", interface wording in "ui").
 
   Activation order matters and is deliberate:
     1. find the DOM prerequisites
     2. parse the JSON
     3. validate the content graph
     4. render the guided interface
     5. only then disable the fallback controls, hide the fallback,
        and set form.noValidate = true
   If any step fails the section keeps a plain, fully working Shopify contact
   form with native browser validation and enabled controls.
   ========================================================================== */
 
(function () {
  'use strict';
 
  if (window.__ibcEnquiryFlowLoaded) return;
  window.__ibcEnquiryFlowLoaded = true;
 
  var NODE_TYPES = ['menu', 'answer', 'form'];
  var FIELD_TYPES = ['text', 'email', 'tel', 'url', 'number', 'date', 'time', 'textarea', 'choice'];
  var ACTION_KINDS = ['call', 'book', 'map', 'review', 'link', 'external', 'mailto', 'node'];
  var TRIMMED_TYPES = ['text', 'email', 'tel', 'url', 'textarea'];
  var REQUIRED_UI = [
    'back', 'startAgain', 'send', 'sending', 'required', 'optional',
    'resolveQuestion', 'resolveYes', 'resolveNo',
    'resolvedTitle', 'resolvedBody', 'resolvedAgain',
    'formTitle', 'formLede', 'privacy', 'privacyLink', 'newTab',
    'callPrefix', 'bookPrefix', 'reviewPrefix', 'mapLabel', 'emailUs',
    'chooseOne', 'genericError'
  ];
 
  /* Analytics "started" is keyed by section id, not by element, so a theme
     editor reload of the same section does not double-count. */
  var startedSections = {};
 
  /* ---------------------------------------------------------------- utils */
 
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }
 
  function deep(obj, dotted) {
    return String(dotted).split('.').reduce(function (o, k) {
      return o == null ? undefined : o[k];
    }, obj);
  }
 
  function todayInLondon() {
    try {
      return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    } catch (err) {
      return new Date().toISOString().slice(0, 10);
    }
  }
 
  /* ------------------------------------------------- fragment handling ----
     The fragment is treated as an ordered list of "key=value" parts joined by
     "&", with bare parts (a plain "#faq") preserved untouched. Only this
     instance's own key is ever added, changed or removed. */
 
  function parseFragment(hash) {
    var raw = String(hash || '').replace(/^#/, '');
    if (!raw) return [];
    return raw.split('&').filter(Boolean).map(function (part) {
      var eq = part.indexOf('=');
      if (eq === -1) return { bare: part };
      return { key: part.slice(0, eq), value: part.slice(eq + 1) };
    });
  }
 
  function buildFragment(parts) {
    var out = parts.map(function (p) {
      return p.bare != null ? p.bare : p.key + '=' + p.value;
    }).filter(Boolean).join('&');
    return out ? '#' + out : '';
  }
 
  function fragmentValue(hash, key) {
    var found = parseFragment(hash).filter(function (p) { return p.key === key; })[0];
    return found ? decodeURIComponent(found.value) : null;
  }
 
  function fragmentWith(hash, key, value) {
    var parts = parseFragment(hash).filter(function (p) { return p.key !== key; });
    if (value != null) parts.push({ key: key, value: value });
    return buildFragment(parts);
  }
 
  /* ------------------------------------------------- content validation --- */
 
  function validateContent(data) {
    var problems = [];
    if (!data || typeof data !== 'object') return ['content is not an object'];
 
    var nodes = data.nodes, fields = data.fields, config = data.config, ui = data.ui;
    if (!nodes || typeof nodes !== 'object') problems.push('nodes block missing');
    if (!fields || typeof fields !== 'object') problems.push('fields block missing');
    if (!config || typeof config !== 'object') problems.push('config block missing');
    if (!ui || typeof ui !== 'object') problems.push('ui block missing');
    if (problems.length) return problems;
 
    REQUIRED_UI.forEach(function (key) {
      if (!ui[key]) problems.push('ui."' + key + '" is missing');
    });
 
    if (!nodes.root) problems.push('root node is missing');
 
    /* derived configuration is computed before tokens are checked, so a
       token such as [[config.shops.soho.phoneFrom]] validates correctly */
    Object.keys(config.shops || {}).forEach(function (key) {
      var shop = config.shops[key];
      if (typeof shop.onlineBookingMax === 'number') {
        shop.phoneFrom = shop.onlineBookingMax + 1;
      }
    });
 
    function checkTokens(value, where) {
      if (typeof value === 'string') {
        (value.match(/\[\[[a-zA-Z0-9_.]+\]\]/g) || []).forEach(function (whole) {
          var path = whole.slice(2, -2);
          if (deep(data, path) == null) {
            problems.push(where + ': token ' + whole + ' does not resolve');
          }
        });
      } else if (Array.isArray(value)) {
        value.forEach(function (v, i) { checkTokens(v, where + '[' + i + ']'); });
      } else if (value && typeof value === 'object') {
        Object.keys(value).forEach(function (k) { checkTokens(value[k], where + '.' + k); });
      }
    }
    checkTokens(nodes, 'nodes');
    checkTokens(fields, 'fields');
    checkTokens(ui, 'ui');
 
    Object.keys(fields).forEach(function (key) {
      var f = fields[key];
      if (FIELD_TYPES.indexOf(f.type) === -1) {
        problems.push('field "' + key + '": unsupported type "' + f.type + '"');
      }
      if (!f.contact) problems.push('field "' + key + '": no contact name');
      if (f.type === 'choice' && !f.options && f.optionsFrom !== 'shops') {
        problems.push('field "' + key + '": choice field has no options');
      }
    });
 
    Object.keys(nodes).forEach(function (id) {
      var n = nodes[id];
 
      if (NODE_TYPES.indexOf(n.type) === -1) {
        problems.push(id + ': unsupported node type "' + n.type + '"');
      }
      if (id !== 'root' && !n.label) problems.push(id + ': missing label');
 
      (n.options || []).concat(n.quiet || []).forEach(function (child) {
        if (!nodes[child]) problems.push(id + ': option references missing node "' + child + '"');
      });
      if (n.type === 'menu' && !(n.options || []).concat(n.quiet || []).length) {
        problems.push(id + ': menu has no options');
      }
 
      (n.actions || []).forEach(function (a) {
        if (ACTION_KINDS.indexOf(a.kind) === -1) {
          problems.push(id + ': unsupported action kind "' + a.kind + '"');
          return;
        }
        if (a.kind === 'node' && !nodes[a.value]) {
          problems.push(id + ': action references missing node "' + a.value + '"');
        }
        var shop = a.shop ? deep(config, 'shops.' + a.shop) : null;
        if (['call', 'book', 'map', 'review'].indexOf(a.kind) > -1) {
          if (!shop) {
            problems.push(id + ': action references missing shop "' + a.shop + '"');
          } else {
            if (a.kind === 'call' && (!shop.tel || !shop.telHref)) {
              problems.push(id + ': shop "' + a.shop + '" needs tel and telHref');
            }
            if (a.kind === 'book' && !shop.bookingUrl) {
              problems.push(id + ': shop "' + a.shop + '" needs bookingUrl');
            }
            if (a.kind === 'map' && (!shop.mapUrl || !shop.address)) {
              problems.push(id + ': shop "' + a.shop + '" needs mapUrl and address');
            }
            if (a.kind === 'review' && !shop.reviewUrl) {
              problems.push(id + ': shop "' + a.shop + '" needs reviewUrl');
            }
          }
        }
        if ((a.kind === 'link' || a.kind === 'external') && !deep(config, 'links.' + a.link + '.url')) {
          problems.push(id + ': action references missing link "' + a.link + '"');
        }
        if (a.kind === 'mailto' && !config.email) {
          problems.push(id + ': mailto action but config.email is missing');
        }
      });
 
      var f = n.form;
      if (f) {
        if (!f.category) problems.push(id + ': form has no category');
        if (!f.topic) problems.push(id + ': form has no topic');
        if (!Array.isArray(f.fields) || !f.fields.length) {
          problems.push(id + ': form has no fields');
        } else {
          var replyFields = 0;
          var contactNames = {};
          f.fields.forEach(function (key) {
            var field = fields[key];
            if (!field) {
              problems.push(id + ': form references missing field "' + key + '"');
              return;
            }
            if (field.contact === 'email') replyFields++;
            /* radio groups intentionally repeat one name across their inputs,
               but two different fields must never share a submitted name */
            if (contactNames[field.contact]) {
              problems.push(id + ': fields "' + contactNames[field.contact] + '" and "' + key +
                '" both submit contact[' + field.contact + ']');
            }
            contactNames[field.contact] = key;
          });
          Object.keys(f.fixed || {}).forEach(function (label) {
            if (contactNames[label]) {
              problems.push(id + ': fixed value "' + label + '" collides with field "' + contactNames[label] + '"');
            }
          });
          if (replyFields !== 1) {
            problems.push(id + ': needs exactly one contact[email] reply field, found ' + replyFields);
          }
          Object.keys(f.conditional || {}).forEach(function (key) {
            var rule = f.conditional[key];
            if (f.fields.indexOf(key) === -1) {
              problems.push(id + ': conditional rule for "' + key + '" is not a field of this form');
            }
            if (!rule || f.fields.indexOf(rule.field) === -1) {
              problems.push(id + ': conditional rule for "' + key + '" depends on a field not in this form');
              return;
            }
            var source = fields[rule.field];
            var options = source && (source.options ||
              (source.optionsFrom === 'shops' ? Object.keys(config.shops).map(function (k) {
                return config.shops[k].name;
              }) : null));
            if (!options || options.indexOf(rule.equals) === -1) {
              problems.push(id + ': conditional rule for "' + key + '" compares against "' + rule.equals +
                '", which is not an option of "' + rule.field + '"');
            }
          });
        }
      }
 
      if (n.type !== 'menu') {
        if (!f && !n.terminal) problems.push(id + ': needs a form or must be terminal');
        if (n.terminal && f) problems.push(id + ': terminal node must not also have a form');
        if (n.terminal && !(n.actions || []).length) {
          problems.push(id + ': terminal node has no usable action');
        }
        if (f && n.type === 'answer' && !n.resolve && !n.alwaysForm) {
          problems.push(id + ': answer has a form that no customer can reach — set resolve or alwaysForm');
        }
      }
    });
 
    /* reachability */
    var seen = {};
    (function walk(id) {
      if (seen[id] || !nodes[id]) return;
      seen[id] = true;
      (nodes[id].options || []).concat(nodes[id].quiet || []).forEach(walk);
      (nodes[id].actions || []).forEach(function (a) {
        if (a.kind === 'node') walk(a.value);
      });
    })('root');
    Object.keys(nodes).forEach(function (id) {
      if (!seen[id]) problems.push(id + ': unreachable from root');
    });
 
    /* unused fields */
    Object.keys(fields).forEach(function (key) {
      var used = Object.keys(nodes).some(function (id) {
        return nodes[id].form && nodes[id].form.fields.indexOf(key) > -1;
      });
      if (!used) problems.push('field "' + key + '" is never used by any route');
    });
 
    return problems;
  }
 
  /* ====================================================== the custom element */
 
  class EnquiryFlow extends HTMLElement {
    constructor() {
      super();
      this.efReady = false;
      this.efAttempts = 0;
      this.efRetry = this.efRetry.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }
 
    connectedCallback() {
      if (this.efReady) return;
      this.efRetry();
    }
 
    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('pageshow', this.onPageShow);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('DOMContentLoaded', this.efRetry);
      this.efReady = false;
      this.efListening = false;
    }
 
    efRetry() {
      if (this.efReady || !this.isConnected) return;
      var missing = false;
      try {
        missing = !this.setup();
      } catch (err) {
        console.error('[enquiry-flow] initialisation failed; the standard contact form remains active.', err);
        this.activateFallback();
        return;
      }
      if (!missing) {
        this.efReady = true;
        return;
      }
      /* prerequisites not in the DOM yet — retry a bounded number of times */
      this.efAttempts++;
      if (this.efAttempts > 3) {
        console.error('[enquiry-flow] required markup or content was never found; the standard contact form remains active.');
        this.activateFallback();
        return;
      }
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.efRetry, { once: true });
      } else {
        window.requestAnimationFrame(this.efRetry);
      }
    }
 
    /* Returns true when the instance is fully live, false when a prerequisite
       is missing and a retry is worthwhile. Throws on a real failure. */
    setup() {
      this.sectionId = this.dataset.sectionId;
      this.cls = this.dataset.classPrefix;
      this.storageKey = this.dataset.storageKey;
      this.fragmentKey = 'enquiry-' + this.sectionId;
 
      this.form = this.querySelector('form');
      this.stepEl = this.querySelector('[data-ef-step]');
      this.crumbEl = this.querySelector('[data-ef-crumbs]');
      this.hiddenEl = this.querySelector('[data-ef-hidden]');
      this.fallbackEl = this.querySelector('[data-ef-fallback]');
      this.errorEl = this.querySelector('[data-ef-errors]');
      var stateEl = this.querySelector('[data-ef-state]');
      this.serverState = stateEl ? stateEl.getAttribute('data-ef-state') : null;
      this.contentEl = document.getElementById(this.dataset.contentId);
 
      if (!this.form || !stateEl) return false;
 
      /* A successful post needs no enhancement at all. */
      if (this.serverState === 'posted') {
        this.clearSaved();
        return true;
      }
 
      if (!this.stepEl || !this.crumbEl || !this.hiddenEl || !this.fallbackEl || !this.contentEl) {
        return false;
      }
 
      var data;
      try {
        data = JSON.parse(this.contentEl.textContent);
      } catch (err) {
        console.error('[enquiry-flow] content JSON did not parse; the standard contact form remains active.', err);
        this.activateFallback();
        return true;
      }
 
      var problems = validateContent(data);
      if (problems.length) {
        console.error('[enquiry-flow] content is invalid; the standard contact form remains active:\n - ' +
          problems.join('\n - '));
        this.activateFallback();
        return true;
      }
 
      this.data = data;
      this.NODES = data.nodes;
      this.FIELDS = data.fields;
      this.CONFIG = data.config;
      this.UI = data.ui;
      this.TODAY = todayInLondon();
      this.answers = {};
      this.route = ['root'];
      this.stage = 'node';
      this.submitting = false;
 
      /* adjacency, so a deep link cannot assemble an impossible route */
      this.childMap = {};
      var self = this;
      Object.keys(this.NODES).forEach(function (id) {
        var n = self.NODES[id];
        var kids = (n.options || []).concat(n.quiet || []);
        (n.actions || []).forEach(function (a) { if (a.kind === 'node') kids.push(a.value); });
        self.childMap[id] = kids;
      });
 
      if (!this.efListening) {
        window.addEventListener('popstate', this.onPopState);
        window.addEventListener('pageshow', this.onPageShow);
        this.form.addEventListener('submit', this.onSubmit);
        this.efListening = true;
      }
 
      /* --------- decide which interface to activate, then activate it ----- */
      var restored = false;
      if (this.serverState === 'error') {
        var saved = this.readSaved();
        if (saved && Array.isArray(saved.route) && this.routeIsValid(saved.route) &&
            this.NODES[saved.nodeId] && this.NODES[saved.nodeId].form &&
            saved.route[saved.route.length - 1] === saved.nodeId) {
          this.route = saved.route;
          this.answers = (saved.answers && typeof saved.answers === 'object') ? saved.answers : {};
          this.stage = 'form';
          this.replaceHistory();
          this.render({ withServerError: true });
          restored = true;
        }
      } else {
        this.clearSaved();
      }
 
      if (!restored) {
        if (this.serverState === 'error') {
          /* the plain form was the one that failed — leave it exactly as
             Shopify rendered it, with native validation intact */
          this.activateFallback();
          return true;
        }
        var fromUrl = this.routeFromLocation();
        if (fromUrl) {
          this.route = fromUrl;
          this.stage = 'node';
          this.replaceHistory();
        }
        /* at the root with no enquiry fragment we do not touch the URL at all */
        this.render();
      }
 
      this.activateGuided();
      return true;
    }
 
    /* ------------------------------------------- interface state switching */
 
    setFallbackEnabled(enabled) {
      if (!this.fallbackEl) return;
      var controls = this.fallbackEl.querySelectorAll('input, textarea, select, button');
      Array.prototype.forEach.call(controls, function (control) {
        control.disabled = !enabled;
      });
      this.fallbackEl.setAttribute('aria-hidden', enabled ? 'false' : 'true');
    }
 
    activateGuided() {
      /* order matters: the guided interface is already rendered by now */
      this.setFallbackEnabled(false);
      this.classList.add('ef--ready');
      this.form.noValidate = true;
    }
 
    activateFallback() {
      this.classList.remove('ef--ready');
      if (this.form) this.form.noValidate = false;
      this.setFallbackEnabled(true);
      if (this.stepEl) this.stepEl.innerHTML = '';
      if (this.hiddenEl) this.hiddenEl.innerHTML = '';
      if (this.crumbEl) {
        this.crumbEl.innerHTML = '';
        this.crumbEl.hidden = true;
      }
      if (this.form) delete this.form.dataset.efNode;
      this.resetSubmitState();
    }
 
    /* --------------------------------------------------------- small utils */
 
    bem(suffix) { return this.cls + '__' + suffix; }
    node(id) { return this.NODES[id]; }
    currentId() { return this.route[this.route.length - 1]; }
    uid(part) { return 'Enquiry-' + this.sectionId + '-' + part; }
 
    token(value) {
      var data = this.data;
      if (typeof value !== 'string') return value;
      return value.replace(/\[\[([a-zA-Z0-9_.]+)\]\]/g, function (whole, path) {
        var found = deep(data, path);
        return found == null ? whole : String(found);
      });
    }
 
    trail() {
      var self = this;
      return this.route.slice(1).map(function (id) {
        return self.node(id) && self.node(id).label;
      }).filter(Boolean);
    }
 
    routeIsValid(candidate) {
      if (!Array.isArray(candidate) || !candidate.length || candidate[0] !== 'root') return false;
      for (var i = 1; i < candidate.length; i++) {
        if (!this.NODES[candidate[i]]) return false;
        if ((this.childMap[candidate[i - 1]] || []).indexOf(candidate[i]) === -1) return false;
      }
      return true;
    }
 
    track(name, detail) {
      try {
        var payload = detail || {};
        payload.event = 'enquiry_' + name;
        this.dispatchEvent(new CustomEvent('enquiry:' + name, { detail: payload, bubbles: true }));
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push(payload);
        }
      } catch (err) { /* analytics must never interrupt the customer */ }
    }
 
    carryOnly() {
      var keep = ['name', 'email', 'phone'];
      var answers = this.answers;
      Object.keys(answers).forEach(function (key) {
        if (keep.indexOf(key) === -1) delete answers[key];
      });
    }
 
    /* -------------------------------------------------- session persistence */
 
    save() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify({
          route: this.route,
          nodeId: this.currentId(),
          answers: this.answers
        }));
      } catch (err) { /* private mode or quota: recovery is a bonus */ }
    }
 
    readSaved() {
      try {
        var raw = sessionStorage.getItem(this.storageKey);
        return raw ? JSON.parse(raw) : null;
      } catch (err) { return null; }
    }
 
    clearSaved() {
      try { sessionStorage.removeItem(this.storageKey); } catch (err) { /* nothing to do */ }
    }
 
    /* ------------------------------------------------------------- history */
 
    myState() {
      var all = (window.history.state && window.history.state.enquiryFlows) || {};
      return all[this.sectionId] || null;
    }
 
    mergedState(entry) {
      var current = (window.history.state && typeof window.history.state === 'object')
        ? window.history.state : {};
      var flows = Object.assign({}, current.enquiryFlows);
      if (entry === null) delete flows[this.sectionId];
      else flows[this.sectionId] = entry;
      return Object.assign({}, current, { enquiryFlows: flows });
    }
 
    urlFor(route) {
      var value = (route && route.length > 1) ? route.slice(1).join('/') : null;
      var hash = fragmentWith(window.location.hash, this.fragmentKey, value);
      return window.location.pathname + window.location.search + hash;
    }
 
    entry(seq) {
      return { route: this.route.slice(), stage: this.stage, seq: seq };
    }
 
    pushHistory() {
      var mine = this.myState();
      var seq = (mine && typeof mine.seq === 'number' ? mine.seq : 0) + 1;
      try {
        window.history.pushState(this.mergedState(this.entry(seq)), '', this.urlFor(this.route));
      } catch (err) { /* history unavailable */ }
    }
 
    replaceHistory() {
      var mine = this.myState();
      var seq = mine && typeof mine.seq === 'number' ? mine.seq : 0;
      try {
        window.history.replaceState(this.mergedState(this.entry(seq)), '', this.urlFor(this.route));
      } catch (err) { /* history unavailable */ }
    }
 
    /* Removes only this instance's entry and fragment, leaving everything
       else on the page untouched. */
    clearHistoryEntry() {
      try {
        var hash = fragmentWith(window.location.hash, this.fragmentKey, null);
        window.history.replaceState(this.mergedState(null), '',
          window.location.pathname + window.location.search + hash);
      } catch (err) { /* history unavailable */ }
    }
 
    routeFromLocation() {
      var value = fragmentValue(window.location.hash, this.fragmentKey);
      if (!value) return null;
      var candidate = ['root'].concat(value.split('/').filter(Boolean));
      return this.routeIsValid(candidate) ? candidate : ['root'];
    }
 
    onPopState(event) {
      if (!this.efReady && !this.data) return;
      var mine = (event.state && event.state.enquiryFlows && event.state.enquiryFlows[this.sectionId]) || null;
      var nextRoute = mine && this.routeIsValid(mine.route) ? mine.route : this.routeFromLocation();
      var nextStage = mine && ['node', 'form', 'resolved'].indexOf(mine.stage) > -1 ? mine.stage : 'node';
 
      if (!nextRoute || !this.routeIsValid(nextRoute)) {
        nextRoute = ['root'];
        nextStage = 'node';
      }
      /* a form stage only makes sense on a node that has one */
      var target = this.NODES[nextRoute[nextRoute.length - 1]];
      if (nextStage === 'form' && (!target || !target.form)) nextStage = 'node';
 
      this.carryOnly();
      this.route = nextRoute;
      this.stage = nextStage;
      this.render();
    }
 
    onPageShow() {
      /* covers both a normal load and a back-forward cache restore, where a
         disabled "Sending…" button would otherwise be frozen */
      if (this.serverState === 'posted') return;
      this.resetSubmitState();
    }
 
    /* ------------------------------------------------------------ rendering */
 
    render(opts) {
      opts = opts || {};
      if (this.stage === 'resolved') return this.renderResolved();
      if (this.stage === 'form') return this.renderFormStage(opts);
      return this.renderNode();
    }
 
    heading(text) {
      var h = el('h3', this.bem('q'), this.token(text));
      h.id = this.uid('step');
      h.setAttribute('tabindex', '-1');
      return h;
    }
 
    backButton(label, handler) {
      var button = el('button', this.bem('back'), label);
      button.type = 'button';
      button.addEventListener('click', handler);
      return button;
    }
 
    renderNode() {
      var self = this;
      var n = this.node(this.currentId());
      if (!n) {
        this.route = ['root'];
        n = this.node('root');
      }
 
      this.renderCrumbs();
      this.stepEl.innerHTML = '';
      this.hiddenEl.innerHTML = '';
      this.resetSubmitState();
      delete this.form.dataset.efNode;
 
      var wrap = el('div', this.bem('stepwrap'));
 
      if (this.route.length > 1) {
        wrap.appendChild(this.backButton('← ' + this.UI.back, function () { self.goBack(); }));
      }
 
      if (n.title) wrap.appendChild(this.heading(n.title));
      if (n.lede) wrap.appendChild(el('p', this.bem('lede'), this.token(n.lede)));
 
      if (n.body) {
        var body = el('div', this.bem('body'));
        n.body.forEach(function (paragraph) {
          body.appendChild(el('p', null, self.token(paragraph)));
        });
        wrap.appendChild(body);
      }
 
      if (n.actions && n.actions.length) this.appendActions(wrap, n.actions);
 
      if (n.type === 'menu') {
        var list = el('ul', this.bem('opts'));
        (n.options || []).forEach(function (id) { list.appendChild(self.optionEl(id, false)); });
        (n.quiet || []).forEach(function (id) { list.appendChild(self.optionEl(id, true)); });
        wrap.appendChild(list);
      }
 
      if (n.alwaysForm && n.form) {
        wrap.appendChild(this.buildForm(n));
      } else if (n.type === 'form' && n.form) {
        wrap.appendChild(this.buildForm(n));
      } else if (n.resolve && n.form) {
        wrap.appendChild(this.resolveEl(n));
      }
 
      if (this.route.length > 1) {
        var restart = this.backButton(this.UI.startAgain, function () { self.restart(); });
        restart.classList.add(this.bem('restart'));
        wrap.appendChild(restart);
      }
 
      this.stepEl.appendChild(wrap);
      this.focusStep(wrap);
      this.announce();
    }
 
    renderFormStage(opts) {
      var self = this;
      var n = this.node(this.currentId());
      if (!n || !n.form) {
        this.stage = 'node';
        return this.renderNode();
      }
 
      this.renderCrumbs();
      this.stepEl.innerHTML = '';
      this.resetSubmitState();
 
      var wrap = el('div', this.bem('stepwrap'));
      wrap.appendChild(this.backButton('← ' + this.UI.back, function () { self.goBack(); }));
      wrap.appendChild(this.heading(this.UI.formTitle));
      wrap.appendChild(el('p', this.bem('lede'), this.UI.formLede));
 
      if (opts && opts.withServerError && this.errorEl) wrap.appendChild(this.errorEl);
 
      wrap.appendChild(this.buildForm(n));
 
      var restart = this.backButton(this.UI.startAgain, function () { self.restart(); });
      restart.classList.add(this.bem('restart'));
      wrap.appendChild(restart);
 
      this.stepEl.appendChild(wrap);
 
      if (opts && opts.withServerError && this.errorEl) this.errorEl.focus();
      else this.focusStep(wrap);
      this.announce();
    }
 
    renderResolved() {
      var self = this;
      this.renderCrumbs();
      this.stepEl.innerHTML = '';
      this.hiddenEl.innerHTML = '';
      this.clearSaved();
      delete this.form.dataset.efNode;
 
      var wrap = el('div', this.bem('stepwrap') + ' ' + this.bem('done'));
      var tick = el('div', this.bem('tick'), '✓');
      tick.setAttribute('aria-hidden', 'true');
      wrap.appendChild(tick);
      wrap.appendChild(this.heading(this.UI.resolvedTitle));
      wrap.appendChild(el('p', this.bem('lede'), this.UI.resolvedBody));
 
      var again = el('button', this.bem('btn') + ' ' + this.bem('btn') + '--ghost', this.UI.resolvedAgain);
      again.type = 'button';
      again.addEventListener('click', function () { self.restart(); });
      wrap.appendChild(again);
 
      this.stepEl.appendChild(wrap);
      this.focusStep(wrap, true);
    }
 
    announce() {
      if (!startedSections[this.sectionId]) {
        startedSections[this.sectionId] = true;
        this.track('started', {});
      }
      this.track('step_viewed', { enquiry_node: this.currentId(), enquiry_stage: this.stage });
    }
 
    focusStep(wrap, always) {
      if (!always && this.route.length <= 1 && this.stage === 'node') return;
      var target = wrap.querySelector('.' + this.bem('q'));
      if (target) target.focus();
    }
 
    focusRootHeading() {
      var h = document.getElementById('EnquiryHeading-' + this.sectionId);
      if (!h) return;
      if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1');
      h.focus();
    }
 
    renderCrumbs() {
      var self = this;
      var labels = this.trail();
      this.crumbEl.innerHTML = '';
      if (!labels.length) {
        this.crumbEl.hidden = true;
        return;
      }
      this.crumbEl.hidden = false;
      var list = el('ol', this.bem('crumb-list'));
      labels.forEach(function (label, index) {
        var item = document.createElement('li');
        if (index) {
          var sep = el('span', self.bem('crumb-sep'), '›');
          sep.setAttribute('aria-hidden', 'true');
          item.appendChild(sep);
        }
        var chip = el('span', self.bem('crumb'), label);
        if (index === labels.length - 1 && self.stage === 'node') {
          chip.setAttribute('aria-current', 'step');
        }
        item.appendChild(chip);
        list.appendChild(item);
      });
      this.crumbEl.appendChild(list);
    }
 
    /* -------------------------------------------------------------- actions */
 
    appendActions(wrap, actions) {
      var self = this;
      var container = el('div', this.bem('actions'));
      var currentGroup = null;
      var groupBox = null;
 
      actions.forEach(function (action) {
        var group = action.group ? self.token(action.group) : null;
        if (group !== currentGroup) {
          currentGroup = group;
          if (group) {
            groupBox = el('div', self.bem('action-group'));
            groupBox.appendChild(el('p', self.bem('action-group-title'), group));
            container.appendChild(groupBox);
          } else {
            groupBox = null;
          }
        }
        (groupBox || container).appendChild(self.actionEl(action));
      });
 
      wrap.appendChild(container);
    }
 
    actionEl(action) {
      var self = this;
      var label = this.token(action.label || '');
      var ghost = action.ghost === true;
 
      if (action.kind === 'node') {
        var button = el('button', this.bem('btn') + ' ' + this.bem('btn') + '--ghost', label);
        button.type = 'button';
        button.addEventListener('click', function () { self.go(action.value); });
        return button;
      }
 
      var anchor = document.createElement('a');
      var shop = action.shop ? this.CONFIG.shops[action.shop] : null;
      var external = false;
      var href = '';
      var quiet = ghost;
 
      if (action.kind === 'call') {
        quiet = true;
        label = label || (this.UI.callPrefix + ' ' + shop.name + ', ' + shop.tel);
        href = 'tel:' + shop.telHref;
      } else if (action.kind === 'book') {
        label = label || (this.UI.bookPrefix + ' ' + shop.name);
        href = shop.bookingUrl;
        external = true;
      } else if (action.kind === 'map') {
        quiet = true;
        label = label || (this.UI.mapLabel + ' — ' + shop.address);
        href = shop.mapUrl;
        external = true;
      } else if (action.kind === 'review') {
        label = label || (this.UI.reviewPrefix + ' ' + shop.name);
        href = shop.reviewUrl;
        external = true;
      } else if (action.kind === 'mailto') {
        label = label || this.UI.emailUs;
        href = 'mailto:' + this.CONFIG.email + '?subject=' + encodeURIComponent(this.token(action.subject || ''));
      } else {
        var link = this.CONFIG.links[action.link];
        href = link.url;
        external = link.external === true;
      }
 
      anchor.className = this.bem('btn') + (quiet ? ' ' + this.bem('btn') + '--ghost' : '');
      anchor.href = href;
      anchor.appendChild(document.createTextNode(label));
 
      if (external) {
        anchor.target = '_blank';
        anchor.rel = 'noopener';
        anchor.appendChild(el('span', this.bem('sr'), ' (' + this.UI.newTab + ')'));
      }
 
      anchor.addEventListener('click', function () {
        self.track('action_clicked', { enquiry_action: action.kind, enquiry_node: self.currentId() });
      });
      return anchor;
    }
 
    optionEl(id, quiet) {
      var self = this;
      var item = document.createElement('li');
      var button = el('button', this.bem('opt') + (quiet ? ' ' + this.bem('opt') + '--quiet' : ''));
      button.type = 'button';
      button.appendChild(document.createTextNode(this.node(id).label));
      var chevron = el('span', this.bem('opt-chev'), '›');
      chevron.setAttribute('aria-hidden', 'true');
      button.appendChild(chevron);
      button.addEventListener('click', function () {
        self.track(self.route.length === 1 ? 'category_selected' : 'option_selected', { enquiry_node: id });
        self.go(id);
      });
      item.appendChild(button);
      return item;
    }
 
    resolveEl(n) {
      var self = this;
      var box = el('div', this.bem('resolve'));
      box.appendChild(el('p', null, this.token(n.resolveLabel || this.UI.resolveQuestion)));
      var row = el('div', this.bem('resolve-row'));
 
      var yes = el('button', this.bem('btn') + ' ' + this.bem('btn') + '--ghost', this.UI.resolveYes);
      yes.type = 'button';
      yes.addEventListener('click', function () {
        self.track('answer_resolved', { enquiry_node: self.currentId() });
        self.stage = 'resolved';
        self.pushHistory();
        self.render();
        self.track('resolved_without_contact', { enquiry_node: self.currentId() });
      });
 
      var no = el('button', this.bem('btn'), this.token(n.resolveNo || this.UI.resolveNo));
      no.type = 'button';
      no.addEventListener('click', function () {
        self.track('continued_to_contact', {
          enquiry_node: self.currentId(),
          enquiry_category: n.form.category,
          enquiry_topic: n.form.topic
        });
        self.stage = 'form';
        self.pushHistory();
        self.render();
      });
 
      row.appendChild(yes);
      row.appendChild(no);
      box.appendChild(row);
      return box;
    }
 
    /* ----------------------------------------------------------- navigation */
 
    go(id) {
      this.carryOnly();
      this.route = this.route.concat([id]);
      this.stage = 'node';
      this.pushHistory();
      this.render();
    }
 
    goBack() {
      var mine = this.myState();
      var seq = mine && typeof mine.seq === 'number' ? mine.seq : 0;
      if (seq > 0) {
        window.history.back();
        return;
      }
      /* the customer arrived here directly, so there is nothing of ours to go
         back to — step up the tree and record that as a new entry */
      this.carryOnly();
      if (this.stage !== 'node') {
        this.stage = 'node';
      } else if (this.route.length > 1) {
        this.route = this.route.slice(0, -1);
      }
      this.pushHistory();
      this.render();
      if (this.route.length === 1) this.focusRootHeading();
    }
 
    restart() {
      this.carryOnly();
      this.answers = {};
      this.route = ['root'];
      this.stage = 'node';
      this.clearSaved();
      /* replace rather than push, so browser Back does not revive the
         abandoned form */
      this.clearHistoryEntry();
      this.render();
      this.focusRootHeading();
    }
 
    /* ---------------------------------------------------------------- forms */
 
    fieldIsActive(n, key) {
      var rule = (n.form.conditional || {})[key];
      if (!rule) return true;
      return this.answers[rule.field] === rule.equals;
    }
 
    applyConditionals(n, container) {
      var self = this;
      Object.keys(n.form.conditional || {}).forEach(function (key) {
        var wrapper = container.querySelector('[data-field="' + key + '"]');
        if (!wrapper) return;
        var active = self.fieldIsActive(n, key);
        wrapper.hidden = !active;
        Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
          input.disabled = !active;
        });
        if (!active) self.clearFieldError(wrapper);
      });
    }
 
    requiredMarker() {
      var mark = el('span', this.bem('req'));
      mark.appendChild(document.createTextNode(' *'));
      var sr = el('span', this.bem('sr'), ' ' + this.UI.required);
      mark.setAttribute('aria-hidden', 'true');
      var holder = document.createDocumentFragment();
      holder.appendChild(mark);
      holder.appendChild(sr);
      return holder;
    }
 
    optionalMarker() {
      return el('span', this.bem('opt-flag'), ' (' + this.UI.optional + ')');
    }
 
    buildInput(key, field, inputId, errorId) {
      var self = this;
      var input;
      if (field.type === 'textarea') {
        input = document.createElement('textarea');
        input.rows = 6;
      } else {
        input = document.createElement('input');
        input.type = field.type;
        if (field.min != null) input.min = field.min;
        if (field.max != null) input.max = field.max;
        if (field.dateMin === 'today') input.min = this.TODAY;
        if (field.dateMax === 'today') input.max = this.TODAY;
      }
 
      input.className = this.bem('input');
      input.id = inputId;
      input.name = 'contact[' + field.contact + ']';
      if (field.maxlength) input.maxLength = field.maxlength;
      if (field.placeholder) input.placeholder = this.token(field.placeholder);
      if (field.autocomplete) input.autocomplete = field.autocomplete;
      if (field.inputmode) input.inputMode = field.inputmode;
      if (field.autocapitalize) input.setAttribute('autocapitalize', field.autocapitalize);
      if (field.spellcheck === false) input.spellcheck = false;
      if (field.required) {
        input.required = true;
        input.setAttribute('aria-required', 'true');
      }
      input.setAttribute('aria-describedby', errorId);
      if (this.answers[key] != null) input.value = this.answers[key];
 
      input.addEventListener('input', function () { self.answers[key] = input.value; });
      input.addEventListener('change', function () { self.answers[key] = input.value; });
      return input;
    }
 
    buildChoice(n, key, field, container) {
      var self = this;
      var wrapper = document.createElement('fieldset');
      wrapper.className = this.bem('field') + ' ' + this.bem('field') + '--choice ' + this.bem('field') + '--wide';
      wrapper.dataset.field = key;
 
      var errorId = this.uid('err-' + key);
      var legend = el('legend', this.bem('label'));
      legend.appendChild(document.createTextNode(this.token(field.label)));
      if (field.required) legend.appendChild(this.requiredMarker());
      else legend.appendChild(this.optionalMarker());
      wrapper.appendChild(legend);
 
      var options = field.optionsFrom === 'shops'
        ? Object.keys(this.CONFIG.shops).map(function (k) { return self.CONFIG.shops[k].name; })
        : field.options;
 
      var row = el('div', this.bem('choice'));
      options.forEach(function (value, index) {
        var item = el('label', self.bem('choice-item'));
        var input = document.createElement('input');
        input.type = 'radio';
        input.className = self.bem('choice-input');
        input.name = 'contact[' + field.contact + ']';
        input.value = value;
        input.id = self.uid('field-' + key + '-' + index);
        /* the error message belongs to the group, but each radio points at it
           so it is announced when any radio takes focus */
        input.setAttribute('aria-describedby', errorId);
        if (field.required) {
          input.required = true;
          input.setAttribute('aria-required', 'true');
        }
        if (self.answers[key] === value) input.checked = true;
        input.addEventListener('change', function () {
          self.answers[key] = value;
          self.clearFieldError(wrapper);
          self.applyConditionals(n, container);
        });
        item.appendChild(input);
        item.appendChild(el('span', self.bem('choice-face'), value));
        row.appendChild(item);
      });
      wrapper.appendChild(row);
 
      var error = el('small', this.bem('err'));
      error.id = errorId;
      wrapper.appendChild(error);
      return wrapper;
    }
 
    clearFieldError(wrapper) {
      wrapper.removeAttribute('aria-invalid');
      var error = wrapper.querySelector('.' + this.bem('err'));
      if (error) {
        error.textContent = '';
        error.classList.remove('is-shown');
      }
      Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
        input.removeAttribute('aria-invalid');
      });
    }
 
    setFieldError(wrapper, message) {
      wrapper.setAttribute('aria-invalid', 'true');
      var error = wrapper.querySelector('.' + this.bem('err'));
      if (error) {
        error.textContent = message;
        error.classList.add('is-shown');
      }
      /* every control in the group carries the invalid state, so it is
         announced whichever one receives focus */
      Array.prototype.forEach.call(wrapper.querySelectorAll('input, textarea, select'), function (input) {
        input.setAttribute('aria-invalid', 'true');
      });
    }
 
    buildForm(n) {
      var self = this;
      var container = document.createElement('div');
      var grid = el('div', this.bem('fields'));
 
      n.form.fields.forEach(function (key) {
        var field = self.FIELDS[key];
        if (!field) return;
 
        if (field.type === 'choice') {
          grid.appendChild(self.buildChoice(n, key, field, container));
          return;
        }
 
        var inputId = self.uid('field-' + key);
        var errorId = self.uid('err-' + key);
        var wrapper = el('div', self.bem('field') +
          (field.type === 'textarea' ? ' ' + self.bem('field') + '--wide' : ''));
        wrapper.dataset.field = key;
 
        var label = el('label', self.bem('label'));
        label.setAttribute('for', inputId);
        label.appendChild(document.createTextNode(self.token(field.label)));
        if (field.required) label.appendChild(self.requiredMarker());
        else label.appendChild(self.optionalMarker());
        wrapper.appendChild(label);
 
        var input = self.buildInput(key, field, inputId, errorId);
        input.addEventListener('input', function () { self.clearFieldError(wrapper); });
        wrapper.appendChild(input);
 
        var error = el('small', self.bem('err'));
        error.id = errorId;
        wrapper.appendChild(error);
 
        grid.appendChild(wrapper);
      });
 
      container.appendChild(grid);
 
      var privacy = el('p', this.bem('privacy'), this.UI.privacy + ' ');
      var privacyLink = document.createElement('a');
      privacyLink.href = this.CONFIG.links.privacy.url;
      privacyLink.textContent = this.UI.privacyLink;
      privacy.appendChild(privacyLink);
      privacy.appendChild(document.createTextNode('.'));
      container.appendChild(privacy);
 
      var submitWrap = el('div', this.bem('submit'));
      var submit = el('button', this.bem('btn'), this.token(n.form.cta || this.UI.send));
      submit.type = 'submit';
      submit.dataset.efSubmit = '';
      submit.dataset.efLabel = submit.textContent;
      submitWrap.appendChild(submit);
      container.appendChild(submitWrap);
 
      this.writeHidden(n);
      this.applyConditionals(n, container);
      this.form.dataset.efNode = this.currentId();
      return container;
    }
 
    /* The categorisation travels as clearly named custom fields. Shopify fixes
       the real notification Subject header, so staff filter on body content. */
    writeHidden(n) {
      var hidden = this.hiddenEl;
      hidden.innerHTML = '';
      var fixed = n.form.fixed || {};
 
      add('Enquiry category', n.form.category);
      add('Enquiry topic', n.form.topic);
      add('Enquiry path', this.trail().join(' > '));
      add('Enquiry reference', this.currentId());
      Object.keys(fixed).forEach(function (key) { add(key, fixed[key]); });
 
      function add(label, value) {
        if (!value) return;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'contact[' + label + ']';
        input.value = value;
        hidden.appendChild(input);
      }
    }
 
    /* ------------------------------------------------------------ validation */
 
    messageFor(field, input) {
      var v = input.validity;
      if (v.valueMissing) return field.error || this.UI.genericError;
      if (v.typeMismatch) return field.typeError || field.error || this.UI.genericError;
      if (v.rangeUnderflow || v.rangeOverflow || v.stepMismatch) {
        return field.rangeError || field.error || this.UI.genericError;
      }
      return field.error || this.UI.genericError;
    }
 
    validate(n) {
      var self = this;
      var firstInvalid = null;
      var invalidKeys = [];
 
      n.form.fields.forEach(function (key) {
        var field = self.FIELDS[key];
        var wrapper = self.stepEl.querySelector('[data-field="' + key + '"]');
        if (!field || !wrapper || wrapper.hidden) return;
 
        self.clearFieldError(wrapper);
 
        if (field.type === 'choice') {
          var checked = wrapper.querySelector('input[type="radio"]:checked');
          self.answers[key] = checked ? checked.value : '';
          if (field.required && !checked) {
            self.setFieldError(wrapper, field.error || self.UI.chooseOne);
            invalidKeys.push(key);
            if (!firstInvalid) firstInvalid = wrapper.querySelector('input[type="radio"]');
          }
          return;
        }
 
        var input = wrapper.querySelector('input, textarea, select');
        if (!input || input.disabled) return;
 
        /* whitespace-only is the same as empty; internal spacing is kept */
        if (TRIMMED_TYPES.indexOf(field.type) > -1) {
          var trimmed = input.value.trim();
          if (trimmed !== input.value) input.value = trimmed;
        }
        self.answers[key] = input.value;
 
        if (!input.checkValidity()) {
          self.setFieldError(wrapper, self.messageFor(field, input));
          invalidKeys.push(key);
          if (!firstInvalid) firstInvalid = input;
        }
      });
 
      return { firstInvalid: firstInvalid, invalidKeys: invalidKeys };
    }
 
    /* ------------------------------------------------------- submit handling */
 
    setBusy() {
      this.submitting = true;
      this.form.setAttribute('aria-busy', 'true');
      var submit = this.stepEl.querySelector('[data-ef-submit]');
      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-disabled', 'true');
        submit.textContent = this.UI.sending;
      }
    }
 
    resetSubmitState() {
      this.submitting = false;
      if (this.form) this.form.removeAttribute('aria-busy');
      if (!this.stepEl) return;
      var submit = this.stepEl.querySelector('[data-ef-submit]');
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-disabled');
        if (submit.dataset.efLabel) submit.textContent = submit.dataset.efLabel;
      }
    }
 
    /* Development assertion. Warns loudly if the data about to be posted
       contains a duplicate of any single-value contact field. Repeated names
       from an unselected radio group are expected and ignored, because a
       radio group contributes at most one entry to FormData. */
    assertSingleValues() {
      var single = ['contact[email]', 'contact[Your name]', 'contact[Message]'];
      var counts = {};
      var data;
      try {
        data = new FormData(this.form);
      } catch (err) {
        return true;
      }
      data.forEach(function (value, name) {
        counts[name] = (counts[name] || 0) + 1;
      });
      var duplicates = single.filter(function (name) { return counts[name] > 1; });
      if (duplicates.length) {
        console.warn('[enquiry-flow] duplicate submitted fields detected: ' + duplicates.join(', ') +
          '. Re-disabling the fallback controls and re-checking.');
        this.setFallbackEnabled(false);
        return false;
      }
      if (!counts['contact[email]']) {
        console.warn('[enquiry-flow] no contact[email] in the submitted data.');
      }
      return true;
    }
 
    onSubmit(event) {
      var nodeId = this.form.dataset.efNode;
      var n = nodeId && this.NODES ? this.NODES[nodeId] : null;
      var guidedFields = this.stepEl && this.stepEl.querySelector('[data-field]');
 
      /* the plain fallback keeps native validation and native behaviour */
      if (!this.classList.contains('ef--ready') || !n || !n.form || !guidedFields) return;
 
      if (this.submitting) {
        event.preventDefault();
        return;
      }
 
      var result = this.validate(n);
      if (result.firstInvalid) {
        event.preventDefault();
        result.firstInvalid.focus();
        this.track('validation_failed', {
          enquiry_node: nodeId,
          enquiry_category: n.form.category,
          enquiry_topic: n.form.topic,
          enquiry_invalid_count: result.invalidKeys.length,
          enquiry_invalid_fields: result.invalidKeys.join(',')
        });
        return;
      }
 
      if (!this.assertSingleValues()) {
        /* self-healed above; check once more before letting it through */
        if (!this.assertSingleValues()) {
          event.preventDefault();
          return;
        }
      }
 
      this.save();
      this.setBusy();
      this.track('submitted', {
        enquiry_node: nodeId,
        enquiry_category: n.form.category,
        enquiry_topic: n.form.topic
      });
    }
  }
 
  if (window.customElements && !window.customElements.get('enquiry-flow')) {
    window.customElements.define('enquiry-flow', EnquiryFlow);
  }
})();
 